import {
  ICoachAvailabilityInput,
  ICoachInput,
  IUpdateCoachInput,
  ICoachAvailabilityHistoryObject,
} from "../../types/api";
import {
  createCoachQueryFn,
  deleteCoachQueryFn,
  getCoachAvailibilityWeeklyQueryFn,
  getCoachByIDQueryFn,
  listCoachesAvailabilityQueryFn,
  listCoachesQueryFn,
  makeCoachAvailableQueryFn,
  updateCoachPriceQueryFn,
  updateCoachHittingPartnerQueryFn,
  updateCoachQueryFn,
  getCoachAvailabilityHistoryQueryFn,
} from "./coachesQueries";
import { API } from "aws-amplify";
import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { toast } from "react-toastify";

export const createCoach = async (coachToCreate: ICoachInput) => {
  const { query, inputs, variables } = createCoachQueryFn(coachToCreate);

  const myQuery = `
        mutation createCoach(${inputs}) {
            ${query}
        }
    `;
  try {
    const getCreateResponse = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const response = getCreateResponse.data.createCoach;
    if ("message" in response) {
      toast.error(response.message);
      return false;
    }
    return true;
  } catch (error: any) {
    toast.error(error.errors[0].message);
    return false;
  }
};

export const listCoaches = async (requestor: "admin" | "user") => {
  const { query } = listCoachesQueryFn(requestor);
  const myQuery = `
        query listCoaches {
            ${query}
        }
    `;
  try {
    const listCoachesResponse = (await API.graphql({
      query: myQuery,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;
    const response = listCoachesResponse.data.listCoaches;
    if ("message" in response) {
      console.error(response.message);
      toast.error(response.message);
      return response;
    }
    return response;
  } catch (error: any) {
    error.errors ? toast.error(error.errors[0].message) : toast.error(error);
    throw error;
  }
};

export const getCoachByID = async (coachId: string) => {
  const { query, inputs, variables } = getCoachByIDQueryFn(coachId);
  const myQuery = `
        query myQuery(${inputs}) {
            ${query}
        }
    `;
  try {
    const getCoachResponse = (await API.graphql({
      query: myQuery,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
      variables: variables,
    })) as GraphQLResult<any>;
    const response = getCoachResponse.data.getCoachByID;
    if ("message" in response) {
      toast.error(response.message);
      return response;
    }
    return response;
  } catch (error: any) {
    error.errors ? toast.error(error.errors[0].message) : toast.error(error);
    throw error;
  }
};

/**
 * The availability write is rejected when the day changed underneath the page
 * — someone booked a time that is inside the interval being edited. Every one
 * of those messages ends in "ladda om sidan", so that is what is matched on
 * rather than three exact strings that would drift apart from the backend.
 *
 * Reloading rather than refetching: the batch editor holds a whole week of
 * intervals in local state built from the stale read, so refreshing the grid
 * under it would leave the form still describing a day that no longer exists.
 */
const STALE_DATA_HINT = "ladda om sidan";

const reloadIfStale = (message?: string) => {
  if (!message?.toLowerCase().includes(STALE_DATA_HINT)) return;
  // Long enough to read the toast before the page goes.
  setTimeout(() => window.location.reload(), 2500);
};

export const makeCoachAvailable = async (
  availabilityToUpdate: ICoachAvailabilityInput
) => {
  const { query, inputs, variables } =
    makeCoachAvailableQueryFn(availabilityToUpdate);

  const myQuery = `
        mutation makeCoachAvailable(${inputs}) {
            ${query}
        }
    `;
  try {
    const getMakeAvailableResponse = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const response = getMakeAvailableResponse.data.makeCoachAvailable;
    if ("message" in response) {
      toast.error(response.message);
      reloadIfStale(response.message);
      return false;
    }
    return true;
  } catch (error: any) {
    // A raised exception in the resolver arrives as a GraphQL error rather
    // than as the Error member of the union, so this branch is where the
    // stale-data rejections actually land.
    const message = error?.errors?.[0]?.message;
    message ? toast.error(message) : toast.error(error);
    reloadIfStale(message);
    return false;
  }
};

export const getCoachAvailibilityWeekly = async (
  coachId: string,
  date: string
) => {
  const { query, inputs, variables } = getCoachAvailibilityWeeklyQueryFn(
    coachId,
    date
  );

  const myQuery = `
        query getCoachAvailibilityWeekly(${inputs}) {
            ${query}
        }
    `;
  try {
    const getWeeklyAvailabilityResponse = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const response =
      getWeeklyAvailabilityResponse.data.getCoachAvailibilityWeekly;
    if ("message" in response) {
      toast.error(response.message);
      return response;
    }
    return response;
  } catch (error: any) {
    toast.error(error.errors[0].message);
    return error;
  }
};

export const listCoachesAvailability = async (date: string) => {
  const { query, inputs, variables } = listCoachesAvailabilityQueryFn(date);
  const myQuery = `
        query listCoachesAvailability(${inputs}) {
            ${query}
        }
    `;
  try {
    const getWeeklyAvailabilityResponse = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const response = getWeeklyAvailabilityResponse.data.listCoachesAvailability;
    if ("message" in response) {
      toast.error(response.message);
      return response;
    }
    return response;
  } catch (error: any) {
    toast.error(error.errors[0].message);
    return error;
  }
};

export const deleteCoach = async (coachId: string) => {
  const { query, inputs, variables } = deleteCoachQueryFn(coachId);
  const myQuery = `
    mutation deleteCoach(${inputs}) {
      ${query}
    }
  `;
  try {
    const getDeleteResponse = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const response = getDeleteResponse.data.deleteCoach;
    if ("message" in response) {
      toast.error(response.message);
      return false;
    }
    return response;
  } catch (error: any) {
    toast.error(error.errors[0].message);
    return false;
  }
};

export const updateCoachPrice = async (coachId: string, price: number) => {
  const { query, inputs, variables } = updateCoachPriceQueryFn(coachId, price);
  const myQuery = `
        mutation updateCoachPrice(${inputs}) {
            ${query}
        }
    `;
  try {
    const getUpdateResponse = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const response = getUpdateResponse.data.updateCoachPrice;
    if ("message" in response) {
      toast.error(response.message);
      return false;
    }
    return true;
  } catch (error: any) {
    toast.error(error.errors[0].message);
    return false;
  }
};

export const updateCoach = async (
  coachId: string,
  inputData: IUpdateCoachInput
) => {
  const { query, inputs, variables } = updateCoachQueryFn(coachId, inputData);
  const myQuery = `
        mutation updateCoach(${inputs}) {
            ${query}
        }
    `;
  try {
    const res = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const response = res.data.updateCoach;
    if (!response || "message" in response) {
      toast.error(response?.message ?? "Kunde inte uppdatera tränare");
      return false;
    }
    return true;
  } catch (error: any) {
    error.errors ? toast.error(error.errors[0].message) : toast.error(error);
    return false;
  }
};

export const updateCoachHittingPartner = async (
  coachId: string,
  isHittingPartner: boolean
) => {
  const { query, inputs, variables } = updateCoachHittingPartnerQueryFn(
    coachId,
    isHittingPartner
  );
  const myQuery = `
        mutation updateCoachHittingPartner(${inputs}) {
            ${query}
        }
    `;
  try {
    const getUpdateResponse = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const response = getUpdateResponse.data.updateCoachHittingPartner;
    if ("message" in response) {
      toast.error(response.message);
      return false;
    }
    return true;
  } catch (error: any) {
    toast.error(error.errors[0].message);
    return false;
  }
};

export const getCoachAvailabilityHistory = async (
  coachId: string,
  fromDate?: string,
  toDate?: string
) => {
  const { query, inputs, variables } = getCoachAvailabilityHistoryQueryFn(
    coachId,
    fromDate,
    toDate
  );
  const myQuery = `
        query myQuery(${inputs}) {
            ${query}
        }
    `;
  try {
    const response = (await API.graphql({
      query: myQuery,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
      variables: variables,
    })) as GraphQLResult<any>;
    const result = response.data.getCoachAvailabilityHistory;
    if ("message" in result) {
      toast.error(result.message);
      return [];
    }
    return (result.history ?? []) as ICoachAvailabilityHistoryObject[];
  } catch (error: any) {
    error.errors ? toast.error(error.errors[0].message) : toast.error(error);
    throw error;
  }
};
